import { Pane, Text } from 'evergreen-ui';
import { isAbsence } from '../../../utils/turniAbsences';

const SHIFT_COLORS = ['#1070CA', '#47B881', '#D9822B', '#9E4BC2', '#EF4E4E', '#17B3A3'];

const timeToAngleDeg = (timeStr) => {
  if (!timeStr) return -90;
  const [h, m] = timeStr.split(':').map(Number);
  return ((h + m / 60) / 24) * 360 - 90;
};

const describeArc = (cx, cy, r, startDeg, endDeg) => {
  const toRad = (d) => (d * Math.PI) / 180;
  const x1 = cx + r * Math.cos(toRad(startDeg));
  const y1 = cy + r * Math.sin(toRad(startDeg));
  const x2 = cx + r * Math.cos(toRad(endDeg));
  const y2 = cy + r * Math.sin(toRad(endDeg));
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`;
};

export const ShiftClock = ({ rawShifts }) => {
  const shifts = rawShifts.filter(
    (t) => !isAbsence(t.shift_type)
  );

  const size = 96;
  const cx = size / 2;
  const cy = size / 2;
  const r = 30;
  const sw = 13;

  const operators = [...new Set(shifts.map((s) => s.operator_name))];

  const hourLabels = [
    { label: '0',  dx: 0,                dy: -(r + sw / 2 + 8) },
    { label: '6',  dx: r + sw / 2 + 8,   dy: 4 },
    { label: '12', dx: 0,                dy: r + sw / 2 + 11 },
    { label: '18', dx: -(r + sw / 2 + 14), dy: 4 },
  ];

  return (
    <Pane display="flex" flexDirection="column" alignItems="center" gap={4} marginTop={8}>
      <svg width={size} height={size} style={{ display: 'block' }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#E4E7EB" strokeWidth={sw} />

        {shifts.map((s, i) => {
          const color = SHIFT_COLORS[operators.indexOf(s.operator_name) % SHIFT_COLORS.length];
          const noTime = !s.start_time || !s.end_time
            || (s.start_time.substring(0, 5) === '00:00' && s.end_time.substring(0, 5) === '00:00');

          if (noTime) {
            return (
              <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={sw} opacity={0.75}>
                <title>{s.operator_name} — tutto il giorno</title>
              </circle>
            );
          }

          let startDeg = timeToAngleDeg(s.start_time);
          let endDeg = timeToAngleDeg(s.end_time);
          if (endDeg <= startDeg) endDeg += 360;

          return (
            <path
              key={i}
              d={describeArc(cx, cy, r, startDeg, endDeg)}
              fill="none"
              stroke={color}
              strokeWidth={sw}
              strokeLinecap="round"
              opacity={0.82}
            >
              <title>{s.operator_name} — {s.start_time.substring(0, 5)}–{s.end_time.substring(0, 5)}</title>
            </path>
          );
        })}

        {[0, 6, 12, 18].map((h) => {
          const deg = (h / 24) * 360 - 90;
          const toRad = (d) => (d * Math.PI) / 180;
          const innerR = r - sw / 2 - 1;
          const outerR = r + sw / 2 + 1;
          return (
            <line
              key={h}
              x1={cx + innerR * Math.cos(toRad(deg))}
              y1={cy + innerR * Math.sin(toRad(deg))}
              x2={cx + outerR * Math.cos(toRad(deg))}
              y2={cy + outerR * Math.sin(toRad(deg))}
              stroke="#8F95B2"
              strokeWidth={1.5}
            />
          );
        })}

        {hourLabels.map(({ label, dx, dy }) => (
          <text
            key={label}
            x={cx + dx}
            y={cy + dy}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={7}
            fill="#8F95B2"
            fontFamily="sans-serif"
          >
            {label}
          </text>
        ))}
      </svg>

      {operators.length > 0 && (
        <Pane display="flex" flexWrap="wrap" justifyContent="center" gap={4} maxWidth={120}>
          {operators.map((op, i) => (
            <Pane key={op} display="flex" alignItems="center" gap={3} title={op}>
              <Pane
                width={8}
                height={8}
                borderRadius="50%"
                style={{ background: SHIFT_COLORS[i % SHIFT_COLORS.length], flexShrink: 0 }}
              />
              <Text size={200} color="muted" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 80 }}>
                {op.split(' ')[0]}
              </Text>
            </Pane>
          ))}
        </Pane>
      )}
    </Pane>
  );
};

export const ShiftBar = ({ rawShifts }) => {
  const shifts = rawShifts.filter(
    (t) => !isAbsence(t.shift_type)
  );
  const operators = [...new Set(shifts.map((s) => s.operator_name))];

  const timeToPercent = (timeStr) => {
    if (!timeStr) return 0;
    const [h, m] = timeStr.split(':').map(Number);
    return ((h + m / 60) / 24) * 100;
  };

  return (
    <Pane marginTop={8}>
      {shifts.length === 0 ? (
        <Pane height={6} background="#E4E7EB" borderRadius={3} />
      ) : (
        operators.map((op, opIdx) => {
          const color = SHIFT_COLORS[opIdx % SHIFT_COLORS.length];
          const opShifts = shifts.filter((s) => s.operator_name === op);
          return (
            <Pane key={op} position="relative" height={6} background="#E4E7EB" borderRadius={3} marginBottom={3}>
              {opShifts.map((s, i) => {
                const noTime = !s.start_time || !s.end_time
                  || (s.start_time.substring(0, 5) === '00:00' && s.end_time.substring(0, 5) === '00:00');
                const left = noTime ? 0 : timeToPercent(s.start_time);
                const right = noTime ? 100 : timeToPercent(s.end_time);
                const width = right > left ? right - left : 100 - left + right;
                return (
                  <Pane
                    key={i}
                    position="absolute"
                    top={0}
                    left={`${left}%`}
                    width={`${Math.max(width, 3)}%`}
                    height="100%"
                    borderRadius={3}
                    style={{ background: color }}
                    title={`${op}: ${s.start_time?.substring(0, 5) || '?'}–${s.end_time?.substring(0, 5) || '?'}`}
                  />
                );
              })}
            </Pane>
          );
        })
      )}
    </Pane>
  );
};
